import { useState } from 'react'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'

/**
 * A list of plain strings an author writes, one at a time.
 *
 * **This is the control a bare `[]string` gets**, and it is shared rather
 * than written per screen because the shape recurs: a select's options, a
 * form's recipients, the slots a tenant-defined type declares. Each is a
 * list of values the author supplies, none of them names anything the
 * platform could offer a picker for, and every one of them was otherwise
 * a property drawn as nothing at all.
 *
 * What it deliberately does not do is reorder. An order that means
 * something belongs to a control that knows what — the placement list
 * drags, the form builder drags — and a list whose order is incidental is
 * better served by add and remove than by handles that imply the order
 * carries weight.
 *
 * A duplicate is refused silently rather than reported: the entry is
 * already visible in the chips above the box, which says it better than a
 * message would.
 */
export function OptionList({
  options,
  label = 'Add an option',
  disabled,
  onChange,
}: {
  options: string[]
  /** The words on the box a new entry is typed into. */
  label?: string
  disabled: boolean
  onChange: (next: string[]) => void
}) {
  const [draft, setDraft] = useState('')

  const add = () => {
    const value = draft.trim()
    if (!value || options.includes(value)) return
    onChange([...options, value])
    setDraft('')
  }

  return (
    <Stack spacing={1}>
      {options.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {options.map((option) => (
            <Chip
              key={option}
              size="small"
              label={option}
              disabled={disabled}
              onDelete={() => onChange(options.filter((entry) => entry !== option))}
            />
          ))}
        </Stack>
      )}
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          label={label}
          value={draft}
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              add()
            }
          }}
        />
        <Button disabled={disabled || !draft.trim()} onClick={add}>
          Add
        </Button>
      </Stack>
    </Stack>
  )
}
